/**
 * Managed Apps API client — wraps `/apps/*` routes served by the
 * AppsFleet RestApi (base-path mapped onto api-${stage}.zibby.app/apps/).
 *
 *   listCatalog()                → { items: [{ appType, displayName, ... }] }
 *   getCatalogEntry(appType)     → { appType, displayName, ... }
 *   deploy({ appType, projectId, name? })  → 202 { instanceId, url, ... }
 *   listInstances()              → { items: [{ instanceId, status, ... }] }
 *   getInstance(instanceId)      → { ... }
 *   destroy(instanceId)          → { instanceId, status: 'stopped' }
 *
 * Catalog browse is public (no JWT). Everything else requires the user
 * to be signed in.
 */

import api from './api';

const BASE = '/apps';

export const Apps = {
  // ── catalog (no auth) ───────────────────────────────────────────────
  listCatalog() {
    return api.get(`${BASE}/catalog`);
  },

  getCatalogEntry(appType) {
    if (!appType) return Promise.reject(new Error('appType is required'));
    return api.get(`${BASE}/catalog/${encodeURIComponent(appType)}`);
  },

  // ── instances ───────────────────────────────────────────────────────
  listInstances() {
    return api.get(`${BASE}`);
  },

  getInstance(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    return api.get(`${BASE}/${encodeURIComponent(instanceId)}`);
  },

  /**
   * Deploy a marketplace app.
   * @param {string} appType    catalog id (e.g. "n8n")
   * @param {string} projectId  project to attach the instance to
   * @param {string} [name]     display name override
   * @returns 202 { instanceId, url, bridgeToken, mcpConfig, ... }
   */
  deploy({ appType, projectId, name } = {}) {
    if (!appType)   return Promise.reject(new Error('appType is required'));
    if (!projectId) return Promise.reject(new Error('projectId is required'));
    const body = { appType, projectId };
    if (name) body.name = name;
    return api.post(`${BASE}/deploy`, body);
  },

  destroy(instanceId) {
    if (!instanceId) return Promise.reject(new Error('instanceId is required'));
    // Backend requires { confirm: true } — same shape as the Lambda
    // direct-invoke contract.
    // `delete` doesn't take a body arg in our client; pass via options.
    return api.delete(`${BASE}/${encodeURIComponent(instanceId)}`, { body: JSON.stringify({ confirm: true }) });
  },
};
